import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { useMemo } from "react";

import { ProgressBar } from "../design/ProgressBar";
import { getCurrentMonthPeriod, periodMonthKey } from "../incentives/periods";
import type { AdvisorGoal, InventoryItem } from "../types";
import { averageDaysInInventory, capturedInMonth } from "./inventoryStats";
import { useMonthCaptures } from "./useMonthCaptures";

/** Generous, fixed page size for the available stock (one bounded query). */
const MAX_STOCK_ITEMS = 5000;

/** Roles that see their own captures against their goal; every other role sees only the aggregate. */
const OWN_PROGRESS_ROLES = ["asesora", "lider"];

/**
 * Almikia Epic EC2d (TASK-139): statistics at the top of the Inventario board. An asesora or lider
 * sees HER OWN captures this month against her `target_captures`; everyone sees the average days
 * in inventory of the available stock. Never a ranking or a per-advisor figure for anyone else,
 * and never a listing date or days on a board row.
 */
export const InventoryStatsCard = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const now = useMemo(() => new Date(), []);
  const monthKey = periodMonthKey(getCurrentMonthPeriod(now));

  const advisorId = identity?.id;
  const showsOwnProgress =
    advisorId != null &&
    OWN_PROGRESS_ROLES.includes(String(identity?.role ?? ""));

  const stock = useGetList<InventoryItem>("inventory_items", {
    pagination: { page: 1, perPage: MAX_STOCK_ITEMS },
    sort: { field: "added_on", order: "DESC" },
    filter: { status: "disponible" },
  });
  const month = useMonthCaptures(monthKey, showsOwnProgress);
  const goals = useGetList<AdvisorGoal>(
    "advisor_goals",
    {
      pagination: { page: 1, perPage: 2 },
      filter: { sales_id: advisorId, period_month: monthKey, scope: "advisor" },
    },
    { enabled: showsOwnProgress },
  );

  if (stock.isPending || month.isPending) return null;
  if (stock.error || month.error) return null;

  const isStockTruncated = (stock.total ?? 0) > (stock.data?.length ?? 0);
  const average = isStockTruncated
    ? null
    : averageDaysInInventory(stock.data ?? [], now);
  const target = goals.data?.[0]?.target_captures ?? null;
  const captured =
    showsOwnProgress && advisorId != null
      ? capturedInMonth(month.options, advisorId, monthKey)
      : 0;

  return (
    <section
      data-testid="inventory-stats"
      className="mb-4 grid gap-4 rounded-2xl border bg-card p-4 md:grid-cols-2"
    >
      {showsOwnProgress ? (
        <div className="flex flex-col gap-2">
          <h2 className="text-base font-bold">
            {translate("crm.inventory_stats.captures_title")}
          </h2>
          {month.isTruncated ? (
            <p className="text-sm text-muted-foreground">
              {translate("crm.inventory_stats.truncated")}
            </p>
          ) : target == null ? (
            <>
              <p className="text-2xl font-bold tabular-nums">
                {translate("crm.inventory_stats.captures_so_far", {
                  smart_count: captured,
                })}
              </p>
              <p className="text-sm text-muted-foreground">
                {translate("crm.goals.no_goal")}
              </p>
            </>
          ) : (
            <>
              <p className="text-2xl font-bold tabular-nums">
                {translate("crm.inventory_stats.captures_progress", {
                  actual: captured,
                  target,
                })}
              </p>
              <ProgressBar
                value={captured}
                max={Math.max(target, captured, 1)}
                target={target}
                label={translate("crm.inventory_stats.captures_bar_label")}
              />
            </>
          )}
        </div>
      ) : null}
      <div className="flex flex-col gap-2">
        <h2 className="text-base font-bold">
          {translate("crm.inventory_stats.average_title")}
        </h2>
        <p className="text-2xl font-bold tabular-nums">
          {isStockTruncated
            ? translate("crm.inventory_stats.truncated")
            : average == null
              ? translate("crm.inventory_stats.average_empty")
              : translate("crm.inventory_stats.average_value", {
                  smart_count: average,
                })}
        </p>
      </div>
    </section>
  );
};
